import React, { useState, useEffect, useRef } from "react";
import "./App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";
import IngredientInput from "./components/IngredientInput";
import DishTypeSelector from "./components/DishTypeSelector";
import RecipeResult from "./components/RecipeResult";
import RecipeHistory from "./components/RecipeHistory";
import AuthModal from "./components/AuthModal";
import MyRasaBox from "./components/MyRasaBox";
import TodaySuggestion from "./components/TodaySuggestion";
import NewsletterPopup from "./components/NewsletterPopup";
import { Leaf, Flame, Droplets, Wind, Sparkles, ChevronDown, Bookmark, BookHeart, LogOut, User } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  // Recipe state
  const [ingredients, setIngredients] = useState([]);
  const [dishType, setDishType] = useState(null);
  const [servings, setServings] = useState(1);
  const [recipe, setRecipe] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  // Auth state
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("rasabox_token"));
  const [savedRecipes, setSavedRecipes] = useState([]);
  const [savedRecipeIds, setSavedRecipeIds] = useState(new Set());
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showMyRasaBox, setShowMyRasaBox] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNewsletterPopup, setShowNewsletterPopup] = useState(false);

  // Today's Suggestion
  const [todaySuggestion, setTodaySuggestion] = useState(null);
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(true);

  const recipeRef = useRef(null);
  const userMenuRef = useRef(null);

  useEffect(() => {
    fetchHistory();
    fetchTodaySuggestion();

    // Newsletter popup — show after 20 seconds if not already subscribed/dismissed
    const alreadySubscribed = localStorage.getItem("rasabox_newsletter");
    const dismissedAt = localStorage.getItem("rasabox_newsletter_dismissed");
    const recentlyDismissed = dismissedAt && (Date.now() - new Date(dismissedAt)) / 86400000 < 7;
    let newsletterTimer = null;
    if (!alreadySubscribed && !recentlyDismissed) {
      newsletterTimer = setTimeout(() => setShowNewsletterPopup(true), 20000);
    }

    const promptHandler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const clickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setShowUserMenu(false);
    };
    window.addEventListener("beforeinstallprompt", promptHandler);
    document.addEventListener("mousedown", clickOutside);

    return () => {
      if (newsletterTimer) clearTimeout(newsletterTimer);
      window.removeEventListener("beforeinstallprompt", promptHandler);
      document.removeEventListener("mousedown", clickOutside);
    };
  }, []);

  // Validate token on load
  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => { setUser(res.data); fetchSavedRecipes(token); })
        .catch(() => { localStorage.removeItem("rasabox_token"); setToken(null); });
    }
  }, []);

  useEffect(() => {
    if (recipe && recipeRef.current) {
      setTimeout(() => recipeRef.current.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [recipe]);

  const fetchTodaySuggestion = async () => {
    try {
      setIsSuggestionLoading(true);
      const res = await axios.get(`${API}/today-suggestion`, { timeout: 35000 });
      setTodaySuggestion(res.data);
    } catch (e) {
      // Silently fail — suggestion is a nice-to-have, not critical
      console.error("Today's suggestion failed:", e);
    } finally {
      setIsSuggestionLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/recipe/history`);
      setHistory(res.data);
    } catch (e) {
      console.error("History fetch failed:", e);
    }
  };

  const fetchSavedRecipes = async (t) => {
    try {
      const res = await axios.get(`${API}/saved-recipes`, {
        headers: { Authorization: `Bearer ${t || token}` },
      });
      setSavedRecipes(res.data);
      setSavedRecipeIds(new Set(res.data.map((s) => s.recipe_id)));
    } catch (e) {
      console.error("Saved recipes fetch failed:", e);
    }
  };

  const handleGenerate = async () => {
    if (ingredients.length === 0) {
      setError("Please add at least one ingredient to your bowl.");
      return;
    }
    setError(null);
    setIsLoading(true);
    setRecipe(null);
    try {
      const res = await axios.post(`${API}/recipe/generate`, { ingredients, dish_type: dishType, servings });
      setRecipe(res.data);
      fetchHistory();
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleHistorySelect = (r) => {
    setRecipe(r);
    setShowHistory(false);
    setShowMyRasaBox(false);
    setTimeout(() => recipeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const handleAuthSuccess = (userData, newToken) => {
    setUser(userData);
    setToken(newToken);
    localStorage.setItem("rasabox_token", newToken);
    setShowAuthModal(false);
    fetchSavedRecipes(newToken);
    toast.success(`Welcome, ${userData.name}!`);
  };

  const handleLogout = () => {
    setUser(null);
    setToken(null);
    setSavedRecipes([]);
    setSavedRecipeIds(new Set());
    localStorage.removeItem("rasabox_token");
    setShowUserMenu(false);
    setShowMyRasaBox(false);
    toast("Signed out. Your recipes are safe.");
  };

  const handleSaveToggle = async (recipeId, recipeData) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    if (savedRecipeIds.has(recipeId)) {
      // Unsave
      try {
        await axios.delete(`${API}/saved-recipes/${recipeId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSavedRecipeIds((prev) => { const s = new Set(prev); s.delete(recipeId); return s; });
        setSavedRecipes((prev) => prev.filter((s) => s.recipe_id !== recipeId));
        toast("Removed from your RasaBox.");
      } catch (e) {
        toast.error("Could not remove recipe.");
      }
    } else {
      // Save
      try {
        await axios.post(`${API}/saved-recipes`, { recipe_id: recipeId, recipe: recipeData }, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSavedRecipeIds((prev) => new Set([...prev, recipeId]));
        fetchSavedRecipes(token);
        toast.success("Saved to your RasaBox!");
      } catch (e) {
        const msg = e.response?.data?.detail;
        if (msg === "Recipe already saved.") toast("Already in your RasaBox.");
        else toast.error("Could not save recipe.");
      }
    }
  };

  const handleUnsave = async (recipeId, recipeData) => {
    await handleSaveToggle(recipeId, recipeData);
  };

  const handleBookmark = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    } else {
      const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent);
      toast(isMobile ? "Add to Home Screen" : "Bookmark RasaBox", {
        description: isMobile
          ? 'Tap the Share / Menu icon in your browser, then "Add to Home Screen".'
          : "Press Ctrl+D (Windows) or Cmd+D (Mac) to bookmark this page.",
        duration: 6000,
      });
    }
  };

  const savedCount = savedRecipes.length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FDFBF7" }}>
      <Toaster
        position="top-center"
        toastOptions={{ style: { fontFamily: "Manrope, sans-serif", backgroundColor: "#FFFFFF", border: "1px solid #E5E0D8", color: "#2D3436" } }}
      />

      {showAuthModal && <AuthModal onSuccess={handleAuthSuccess} onClose={() => setShowAuthModal(false)} />}
      {showNewsletterPopup && <NewsletterPopup onClose={() => setShowNewsletterPopup(false)} />}

      {/* Header */}
      <header className="sticky top-0 z-40" style={{ backgroundColor: "rgba(253,251,247,0.88)", backdropFilter: "blur(12px)", borderBottom: "1px solid #E5E0D8" }}>
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between gap-2">
          {/* Logo */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: "#E07A5F" }}>
              <Leaf size={16} color="white" strokeWidth={1.5} />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-serif font-bold text-base" style={{ color: "#2D3436" }}>RasaBox</span>
              <span className="text-xs" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>by ThatAyurvedaGuy</span>
            </div>
          </div>

          {/* Right controls */}
          <div className="flex items-center gap-2">
            {/* Bookmark */}
            <button data-testid="bookmark-btn" onClick={handleBookmark} title="Bookmark"
              className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-full transition-all hover:scale-105"
              style={{ backgroundColor: "#F4F1EA", color: "#5D6D7E" }}>
              <Bookmark size={14} />
              <span className="hidden sm:inline">Bookmark</span>
            </button>

            {/* My RasaBox (logged in only) */}
            {user && (
              <button data-testid="my-rasabox-btn"
                onClick={() => { setShowMyRasaBox(!showMyRasaBox); setShowHistory(false); }}
                className="flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-full transition-all hover:scale-105"
                style={{
                  backgroundColor: showMyRasaBox ? "#E07A5F" : "#FFF0EB",
                  color: showMyRasaBox ? "#FFFFFF" : "#E07A5F",
                  border: "1px solid #E07A5F40",
                }}>
                <BookHeart size={14} />
                <span className="hidden sm:inline">My RasaBox</span>
                {savedCount > 0 && (
                  <span className="text-xs px-1.5 py-0.5 rounded-full font-bold"
                    style={{ backgroundColor: showMyRasaBox ? "rgba(255,255,255,0.25)" : "#E07A5F", color: "#FFFFFF" }}>
                    {savedCount}
                  </span>
                )}
              </button>
            )}

            {/* Auth: sign in button OR user menu */}
            {!user ? (
              <button data-testid="sign-in-btn" onClick={() => setShowAuthModal(true)}
                className="flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-full transition-all hover:scale-105"
                style={{ backgroundColor: "#E07A5F", color: "#FFFFFF" }}>
                <User size={14} />
                <span>Sign In</span>
              </button>
            ) : (
              <div className="relative" ref={userMenuRef}>
                <button data-testid="user-menu-btn"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-full transition-all hover:scale-105"
                  style={{ backgroundColor: "#F4F1EA", color: "#5D6D7E" }}>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: "#E07A5F", color: "#FFFFFF" }}>
                    {user.name[0].toUpperCase()}
                  </div>
                  <span className="hidden sm:inline">{user.name.split(" ")[0]}</span>
                  <ChevronDown size={12} style={{ transform: showUserMenu ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-1 w-44 rounded-2xl overflow-hidden z-50 animate-fade-in"
                    style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E0D8", boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}>
                    <div className="px-4 py-3" style={{ borderBottom: "1px solid #F4F1EA" }}>
                      <p className="text-sm font-semibold truncate" style={{ color: "#2D3436", fontFamily: "Manrope, sans-serif" }}>{user.name}</p>
                      <p className="text-xs truncate mt-0.5" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>{user.email}</p>
                    </div>
                    <button data-testid="logout-btn" onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm transition-colors hover:bg-muted"
                      style={{ color: "#C0392B", fontFamily: "Manrope, sans-serif" }}>
                      <LogOut size={14} /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Past Recipes */}
            <button data-testid="history-toggle-btn"
              onClick={() => { setShowHistory(!showHistory); setShowMyRasaBox(false); }}
              className="flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full transition-all hover:scale-105"
              style={{ backgroundColor: "#F4F1EA", color: "#5D6D7E" }}>
              <span>Past Recipes</span>
              <ChevronDown size={14} style={{ transform: showHistory ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
            </button>
          </div>
        </div>
      </header>

      {/* Sliding panels */}
      {showHistory && (
        <div className="animate-fade-in">
          <RecipeHistory history={history} onSelect={handleHistorySelect} />
        </div>
      )}
      {showMyRasaBox && user && (
        <div className="animate-fade-in">
          <MyRasaBox savedRecipes={savedRecipes} onSelect={handleHistorySelect} onUnsave={handleUnsave} />
        </div>
      )}

      {/* Hero Section */}
      <section className="max-w-4xl mx-auto px-6 pt-16 pb-8 text-center">
        <div className="animate-fade-up opacity-0" style={{ animationFillMode: "forwards" }}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-widest mb-6"
            style={{ backgroundColor: "#F2CC8F33", color: "#B8860B", border: "1px solid #F2CC8F" }}>
            <Sparkles size={12} />
            <span>Powered by Ayurvedic Wisdom</span>
          </div>
          <h1 className="font-serif font-bold mb-4" style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)", color: "#2D3436", lineHeight: 1.15 }}>
            What's in your kitchen<br />
            <span style={{ color: "#E07A5F", fontStyle: "italic" }}>right now?</span>
          </h1>
          <p className="text-base mx-auto max-w-xl leading-relaxed" style={{ color: "#5D6D7E", fontFamily: "Manrope, sans-serif" }}>
            Tell me the ingredients you have, and I'll craft a warm, nourishing Ayurvedic recipe
            that respects your body's nature — avoiding incompatible combinations and balancing your Doshas.
          </p>
        </div>
      </section>

      {/* Input Bowl */}
      <section className="max-w-4xl mx-auto px-6 pb-16">
        <div className="rounded-3xl p-8 md:p-10 shadow-card relative overflow-hidden animate-fade-up opacity-0 stagger-2"
          style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E0D8", animationFillMode: "forwards" }}>
          <div className="absolute top-0 right-0 w-40 h-40 opacity-10 pointer-events-none"
            style={{ background: "radial-gradient(circle at top right, #F2CC8F, transparent)" }} />

          <div className="mb-8">
            <label className="block text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#5D6D7E" }}>
              Ingredients in your kitchen
            </label>
            <IngredientInput ingredients={ingredients} setIngredients={setIngredients} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#5D6D7E" }}>
                What are you in the mood for? <span className="lowercase normal-case font-normal" style={{ color: "#9AA5B1" }}>(optional)</span>
              </label>
              <DishTypeSelector selected={dishType} onSelect={setDishType} />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#5D6D7E" }}>
                Serves how many?
              </label>
              <div className="flex gap-3" data-testid="serving-selector">
                {[1, 2, 3].map((n) => (
                  <button key={n} data-testid={`serving-${n}`} onClick={() => setServings(n)}
                    className="w-12 h-12 rounded-xl font-semibold text-base transition-all hover:scale-105"
                    style={{
                      backgroundColor: servings === n ? "#E07A5F" : "#F4F1EA",
                      color: servings === n ? "#FFFFFF" : "#5D6D7E",
                      border: servings === n ? "2px solid #E07A5F" : "2px solid transparent",
                    }}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 px-4 py-3 rounded-xl text-sm" style={{ backgroundColor: "#FEF2F2", color: "#C0392B", border: "1px solid #FECACA" }}>
              <span className="font-medium">Disharmony detected: </span>{error}
            </div>
          )}

          <button data-testid="generate-recipe-btn" onClick={handleGenerate}
            disabled={isLoading || ingredients.length === 0}
            className="w-full py-4 rounded-2xl font-semibold text-lg transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            style={{
              backgroundColor: isLoading ? "#C4A882" : "#E07A5F",
              color: "#FFFFFF",
              boxShadow: isLoading ? "none" : "0 8px 30px rgba(224,122,95,0.3)",
              fontFamily: "Manrope, sans-serif",
            }}>
            {isLoading ? (
              <span className="flex items-center justify-center gap-3">
                <span className="animate-stir inline-block"><Leaf size={20} /></span>
                <span className="animate-pulse-gentle">Finding your balance...</span>
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Sparkles size={18} /> Find My Balance
              </span>
            )}
          </button>

          {!isLoading && ingredients.length === 0 && (
            <p className="text-center text-xs mt-4" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>
              Try: rice, ghee, cumin, ginger, lentils...
            </p>
          )}
        </div>

        <div className="flex flex-wrap justify-center gap-6 mt-6 text-xs animate-fade-up opacity-0 stagger-3"
          style={{ animationFillMode: "forwards", color: "#9AA5B1", fontFamily: "Manrope, sans-serif" }}>
          <span className="flex items-center gap-1.5"><Leaf size={12} style={{ color: "#81B29A" }} /> Always warm &amp; moist</span>
          <span className="flex items-center gap-1.5"><Flame size={12} style={{ color: "#E07A5F" }} /> No Virudh-ahaar</span>
          <span className="flex items-center gap-1.5"><Droplets size={12} style={{ color: "#7EB3D4" }} /> Tridoshic by default</span>
        </div>
      </section>

      {/* Today's Suggestion */}
      <div className="max-w-4xl mx-auto px-6 -mt-8 mb-6">
        <TodaySuggestion
          data={todaySuggestion}
          isLoading={isSuggestionLoading}
          onCookThis={(r) => {
            setRecipe(r);
            setTimeout(() => recipeRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
          }}
        />
      </div>

      {/* Recipe Result */}
      {(recipe || isLoading) && (
        <div ref={recipeRef} className="max-w-4xl mx-auto px-6 pb-20">
          {isLoading ? (
            <LoadingSkeleton />
          ) : (
            <div className="animate-fade-up opacity-0" style={{ animationFillMode: "forwards" }}>
              <RecipeResult
                recipe={recipe}
                isSaved={savedRecipeIds.has(recipe?.id)}
                onSaveToggle={handleSaveToggle}
                user={user}
              />
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <footer className="py-8 text-xs" style={{ color: "#9AA5B1", fontFamily: "Manrope, sans-serif", borderTop: "1px solid #E5E0D8" }}>
        <div className="max-w-4xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-center sm:text-left">
            <p>Rooted in Charaka Samhita &amp; Ashtanga Hridayam</p>
            <p className="mt-0.5">Always consult a qualified Vaidya for specific health concerns.</p>
          </div>
          <a data-testid="find-prakriti-link" href="https://roadmap.thatayurvedaguy.com" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-xs transition-all hover:scale-105 whitespace-nowrap"
            style={{ backgroundColor: "#F2CC8F33", color: "#8B6914", border: "1px solid #F2CC8F80" }}>
            <Sparkles size={11} /> Find your Prakriti
          </a>
        </div>
      </footer>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="rounded-3xl p-8 overflow-hidden" style={{ backgroundColor: "#FFFFFF", border: "1px solid #E5E0D8" }}>
      <div className="shimmer h-8 w-2/3 rounded-lg mb-3" />
      <div className="shimmer h-4 w-1/2 rounded-lg mb-8" />
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="shimmer h-32 rounded-2xl" />
        <div className="shimmer h-32 rounded-2xl" />
      </div>
      <div className="shimmer h-4 w-full rounded-lg mb-2" />
      <div className="shimmer h-4 w-5/6 rounded-lg mb-2" />
      <div className="shimmer h-4 w-4/6 rounded-lg" />
    </div>
  );
}

export default App;
